import { PermissionRequest } from "@p0security/shared/types/permission";
import {
  ActiveRequestStatuses,
  PendingRequestStatuses,
} from "@p0security/shared/types/request-status";
import { Empty, Grid, List, Pagination, Spin, Typography } from "antd";
import { useAuthFetch } from "components/Login/hook";
import { usePagination } from "hooks/usePagination";
import { FirestoreDoc } from "providers/FirestoreProvider";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router";

import { ActiveRequestActions } from "../components/ActiveRequestActions";
import { ApproveRequestModal } from "../components/ApproveRequestModal";
import { PendingRequestActions } from "../components/PendingRequestActions";
import { RequestDetailDrawer } from "../components/RequestDetailsDrawer";
import { RequestFilters } from "../components/RequestFilters";
import { RequestPreview } from "../components/RequestPreview";
import { StyledRequestControls } from "../components/StyledRequestControls";
import { IntegrationsContext } from "../contexts/IntegrationsContext";
import { useActiveAndPendingPagination } from "../hooks/useActiveAndPendingPagination";
import { useFilteredData } from "../hooks/useFilteredData";
import { usePermissionRequestFilterOptions } from "../hooks/useRequestFilterOptions";
import { useRequests } from "../hooks/useRequests";
import { handleRequestActionResponse } from "../requestUtils";
import { PERMISSION_REQUEST_FILTER_CONFIG } from "../utils/filterConfigs";
import { useManageableRequests } from "./use-manageable-requests";

const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_SIZE = 10;

export const ActiveRequests: React.FC = () => {
  const { requestId } = useParams();

  const activeRequests = useRequests(ActiveRequestStatuses);
  const pendingRequests = useRequests(PendingRequestStatuses);

  const { isManageable, refresh, isPending } = useManageableRequests();

  useEffect(() => {
    refresh();
  }, [refresh, activeRequests.requests, pendingRequests.requests]);

  const integrations = useContext(IntegrationsContext);

  // Combine all requests so all can be filtered together
  const allRequests = useMemo(
    () => [...activeRequests.requests, ...pendingRequests.requests],
    [activeRequests.requests, pendingRequests.requests]
  );

  const options = usePermissionRequestFilterOptions(allRequests);

  const { filters, applyFilters, resetFilters } = useFilteredData<
    FirestoreDoc<PermissionRequest>
  >(PERMISSION_REQUEST_FILTER_CONFIG);

  const { sm } = Grid.useBreakpoint();

  const [requestProcessing, setRequestProcessing] = useState(false);
  const [requestInModal, setRequestInModal] =
    useState<FirestoreDoc<PermissionRequest>>();
  const [pagination, _, { handlePageChange }] = usePagination();
  const authFetch = useAuthFetch(undefined, setRequestProcessing);

  const { page, pageSize } = useMemo(
    () => ({
      page: pagination.page ?? DEFAULT_PAGE,
      pageSize: pagination.pageSize ?? DEFAULT_PAGE_SIZE,
    }),
    [pagination]
  );

  const handleDeny = useCallback(
    async (requestId: string) => {
      handleRequestActionResponse(
        await authFetch(`permission-requests/${requestId}/deny`, {
          method: "POST",
        }),
        "Request denied"
      );
    },
    [authFetch]
  );

  const handleRevoke = useCallback(
    async (requestId: string) => {
      handleRequestActionResponse(
        await authFetch(`permission-requests/${requestId}/revoke`, {
          method: "POST",
        }),
        "Access revoked"
      );
    },
    [authFetch]
  );

  const renderActiveRequest = useCallback(
    (item: FirestoreDoc<PermissionRequest>) => {
      return (
        <StyledRequestControls
          $isDesktop={sm}
          actions={ActiveRequestActions(
            item,
            () => handleRevoke(item.id),
            requestProcessing
          )}
        >
          <RequestPreview requestId={item.id} request={item.data} />
        </StyledRequestControls>
      );
    },
    [handleRevoke, requestProcessing, sm]
  );

  const renderPendingRequest = useCallback(
    (item: FirestoreDoc<PermissionRequest>) => {
      return (
        <StyledRequestControls
          $isDesktop={sm}
          actions={PendingRequestActions(
            () => setRequestInModal(item),
            () => handleDeny(item.id)
          )}
        >
          <RequestPreview requestId={item.id} request={item.data} />
        </StyledRequestControls>
      );
    },
    [handleDeny, sm]
  );

  const closeModal = useCallback(() => {
    setRequestInModal(undefined);
  }, []);

  // Apply manageable filter first, then apply other filters
  const manageableActiveRequests = useMemo(
    () =>
      activeRequests.requests.filter((req) =>
        isManageable({ anyOf: ["revokable"] }, req.id)
      ),
    [activeRequests.requests, isManageable]
  );

  const manageablePendingRequests = useMemo(
    () =>
      pendingRequests.requests.filter((req) =>
        isManageable({ anyOf: ["approvable", "rejectable"] }, req.id)
      ),
    [pendingRequests.requests, isManageable]
  );

  // Apply all filters
  const filteredActiveRequests = useMemo(
    () => applyFilters(manageableActiveRequests),
    [manageableActiveRequests, applyFilters]
  );

  const filteredPendingRequests = useMemo(
    () => applyFilters(manageablePendingRequests),
    [manageablePendingRequests, applyFilters]
  );

  // Apply pagination to each section independently
  const {
    paginatedActiveSection: paginatedActiveRequests,
    paginatedPendingSection: paginatedPendingRequests,
    totalItems,
  } = useActiveAndPendingPagination(
    filteredActiveRequests,
    filteredPendingRequests,
    { page, pageSize }
  );

  const showAnyRequests = useMemo(
    () =>
      filteredActiveRequests.length > 0 || filteredPendingRequests.length > 0,
    [filteredActiveRequests, filteredPendingRequests]
  );

  return (
    <>
      <div style={{ maxWidth: 1500 }}>
        <RequestFilters
          filters={filters}
          options={options}
          hideApprovalDateFilter={false}
          hideExport={true}
          resetFilters={resetFilters}
        />
        <Typography.Title level={4}>Active</Typography.Title>
        <div style={{ minHeight: "22em" }}>
          {activeRequests.loading ||
          isPending ||
          Object.values(integrations).some(
            (integration) => integration.loading
          ) ? (
            <Spin />
          ) : showAnyRequests ? (
            paginatedActiveRequests.length > 0 ? (
              <List
                dataSource={paginatedActiveRequests}
                renderItem={renderActiveRequest}
                pagination={false}
              />
            ) : (
              <Empty description="No active requests on this page" />
            )
          ) : (
            <Empty description="No active requests" />
          )}
        </div>
        <Typography.Title level={4}>Pending</Typography.Title>
        <div style={{ minHeight: "22em" }}>
          {pendingRequests.loading ||
          isPending ||
          Object.values(integrations).some(
            (integration) => integration.loading
          ) ? (
            <Spin />
          ) : showAnyRequests ? (
            paginatedPendingRequests.length > 0 ? (
              <List
                dataSource={paginatedPendingRequests}
                renderItem={renderPendingRequest}
                pagination={false}
              />
            ) : (
              <Empty description="No pending requests on this page" />
            )
          ) : (
            <Empty description="No pending requests" />
          )}
        </div>
        {showAnyRequests && (
          <Pagination
            current={page}
            onChange={handlePageChange}
            pageSize={pageSize}
            showSizeChanger
            size="small"
            style={{ marginTop: 16, textAlign: "right" }}
            total={totalItems}
          />
        )}
      </div>
      <ApproveRequestModal
        request={requestInModal}
        loading={requestProcessing}
        onClose={closeModal}
      />
      {requestId && <RequestDetailDrawer requestId={requestId} />}
    </>
  );
};
